import { Item, ItemActions, ItemDescription, ItemHeader, ItemTitle } from '@ibsys/kit';
import { IconButton, Tooltip, TruncatedText } from '@/shared/ui';
import { BookmarkXIcon, Share2Icon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { DashboardPresetItem as DashboardPresetItemData } from '@/entities/settings';
import { cn, getFullName } from '@/shared/lib';

type Props = {
  preset: DashboardPresetItemData;
  /** Применённый пресет — подсвечивается */
  isActive: boolean;
  onApply: () => void;
  /** Снять применённый пресет; кнопка показывается только у него */
  onClearActive: () => void;
};

/** Строка дашборда-пресета в списке: применение по клику и снятие применённого. */
export const DashboardPresetItem: FC<Props> = ({ preset, isActive, onApply, onClearActive }) => {
  const { t } = useTranslation('qualityDashboard');
  const sharedLabel =
    preset.owner && !preset.isEditable
      ? t('presets.sharedBy', { owner: getFullName(preset.owner.firstName, preset.owner.lastName) })
      : t('presets.sharedDashboard');

  return (
    <Item
      variant="outline"
      size="sm"
      role="button"
      tabIndex={-1}
      onClick={onApply}
      className={cn(
        'group cursor-pointer hover:bg-muted/60 p-2 w-full min-w-0',
        isActive && 'border-primary bg-primary/5',
      )}
    >
      <ItemHeader className="min-w-0 min-h-9 gap-2">
        {preset.isShared && (
          <Tooltip tooltip={sharedLabel}>
            <span aria-label={sharedLabel} className="shrink-0 text-muted-foreground">
              <Share2Icon className="size-3.5" />
            </span>
          </Tooltip>
        )}
        <ItemTitle className="block w-full min-w-0 text-sm">
          <TruncatedText>{preset.name}</TruncatedText>
        </ItemTitle>
        <ItemActions className="shrink-0" onClick={(e) => e.stopPropagation()}>
          {isActive && (
            <IconButton
              variant="ghost"
              size="icon"
              label={t('presets.clearActive')}
              icon={<BookmarkXIcon className="size-3.5" />}
              onClick={onClearActive}
            />
          )}
        </ItemActions>
      </ItemHeader>
      <ItemDescription className="text-xs text-muted-foreground">
        {t('presets.chartsCount', { count: preset.charts.length })}
      </ItemDescription>
    </Item>
  );
};
